var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useState, useEffect } from 'react';
import styled, { css } from '../../../../styled';
import TooltipView, { TooltipWrapper } from '../../../tooltip/TooltipView';
import * as Tooltip from '../../../tooltip';
import * as Icons from '../../../icons';
import { StyledInput, Wrapper as WrapperComponent, InputWrapper as InputWrapperComponent, } from '../../../input';
export const DateInputIconWrapper = styled.div `
  display: ${({ show }) => (show ? 'flex' : 'none')}};
  align-items: center;
  justify-content: center;
  font-size: ${({ inputSize, theme }) => theme.inputs.size[inputSize].fontSize};
  color: ${({ theme }) => theme.colors.INK_500}};
  width: 2.25rem;
  height: 2.25rem;
  box-sizing: border-box;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  border-left: 0.0625rem solid
    ${({ theme }) => theme.colors[theme.inputs.status.default.borderColor]};

  ${({ disabled }) => !disabled &&
    css `
      ${StyledInput} {
        cursor: pointer;
      }
    `};
`;
export const ClearBtnWrapper = styled(DateInputIconWrapper) `
  &:hover {
    background-color: ${({ theme }) => theme.colors.PRIMARY_100}};
    border-left: 0.0625rem solid
      ${({ theme }) => theme.colors[theme.inputs.status.hover.borderColor]};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.PRIMARY_300}};
    border-left: 0.0625rem solid
      ${({ theme }) => theme.colors[theme.inputs.status.hover.borderColor]};
  }

  &:hover svg > path {
    fill: ${({ theme }) => theme.colors.PRIMARY_600};
  }

  &:active svg > path {
    fill: ${({ theme }) => theme.colors.PRIMARY_700};
  }
`;
export const Wrapper = styled(WrapperComponent) `
  flex: 1;
  padding-right: 0;
  cursor: default;

  ${({ block }) => block &&
    css `
      ${StyledInput} {
        width: 100%;
      }
    `};
`;
export const InputWrapper = styled(InputWrapperComponent) `
  flex: 1;
  padding-right: 0.5rem;

  ${TooltipWrapper} > span {
    max-width: unset;
    white-space: nowrap;
  }

  ${StyledInput} {
    flex: 1;
  }

  ${({ disabled }) => !disabled &&
    css `
      ${StyledInput} {
        cursor: text;
      }
    `};
`;
const DateInput = (_a) => {
    var { lang, status = 'valid', block = false, disabled, inputRef = React.createRef(), inputSize = 'MEDIUM', tooltipText, tooltipDirection = 'TOP_CENTER', clearBtnTooltipText, onClick, onCalendarIconClick, onClearBtnClick, Icon = Icons.Outline.Calendar, value } = _a, restProps = __rest(_a, ["lang", "status", "block", "disabled", "inputRef", "inputSize", "tooltipText", "tooltipDirection", "clearBtnTooltipText", "onClick", "onCalendarIconClick", "onClearBtnClick", "Icon", "value"]);
    const [isFocus, setIsFocus] = useState(false);
    const [isShowTooltip, setIsShowTooltip] = useState(false);
    const [isShowClearBtn, setIsShowClearBtn] = useState(false);
    const handleBlur = useCallback(() => {
        setIsFocus(false);
        setIsShowTooltip(false);
    }, []);
    const handleWrapperClick = useCallback(() => {
        if (typeof inputRef === 'function')
            return;
        if (!inputRef || !inputRef.current)
            return;
        setIsFocus(true);
        inputRef.current.focus();
    }, [inputRef]);
    const handleWrapperMouseEnter = useCallback(() => {
        if (value)
            setIsShowClearBtn(true);
    }, [value]);
    const handleWrapperMouseLeave = useCallback(() => {
        setIsShowClearBtn(false);
    }, []);
    const handleClearBtnClick = useCallback((e) => {
        e.stopPropagation();
        if (typeof inputRef === 'function')
            return;
        if (!inputRef || !inputRef.current)
            return;
        inputRef.current.focus();
        setIsShowClearBtn(false);
        setIsShowTooltip(true);
        if (onClearBtnClick)
            onClearBtnClick(e);
    }, [inputRef, onClearBtnClick]);
    useEffect(() => {
        if (typeof inputRef === 'function')
            return undefined;
        if (!inputRef || !inputRef.current)
            return undefined;
        const element = inputRef.current;
        const handleFocus = () => {
            setIsFocus(true);
            setIsShowTooltip(element.value.length === 0);
        };
        const handleKeyup = (e) => {
            if (e.keyCode === 13) {
                element.blur();
                return;
            }
            setIsShowTooltip(isFocus && element.value.length === 0);
        };
        // init
        element.addEventListener('keyup', handleKeyup);
        element.addEventListener('focus', handleFocus);
        element.addEventListener('blur', handleBlur);
        return () => {
            element.removeEventListener('keyup', handleKeyup);
            element.removeEventListener('focus', handleFocus);
            element.removeEventListener('blur', handleBlur);
        };
    }, [handleBlur, inputRef, isFocus]);
    useEffect(() => {
        // hide tooltip after selecting a new valid date from calendar view
        if (value)
            setIsShowTooltip(false);
    }, [value]);
    return (React.createElement(Wrapper, { disabled: disabled, focused: isFocus, onClick: handleWrapperClick, onMouseEnter: handleWrapperMouseEnter, onMouseLeave: handleWrapperMouseLeave, onBlur: handleBlur, status: status, block: block },
        React.createElement(InputWrapper, { disabled: disabled, onClick: onClick },
            React.createElement(TooltipView, { type: "primary", lang: lang, text: tooltipText, show: isShowTooltip, direction: tooltipDirection, containerClassName: "tooltip-container" },
                React.createElement(StyledInput, Object.assign({ type: "input", ref: inputRef, status: "valid", disabled: disabled, inputSize: inputSize, value: value }, restProps)))),
        React.createElement(Tooltip.Primary, { lang: lang, text: clearBtnTooltipText, direction: tooltipDirection },
            React.createElement(ClearBtnWrapper, { show: isShowClearBtn, inputSize: inputSize, onClick: handleClearBtnClick },
                React.createElement(Icons.Outline.Cross, { color: "INK_600", size: "SMALL" }))),
        React.createElement(DateInputIconWrapper, { show: !isShowClearBtn, disabled: disabled, inputSize: inputSize, onClick: onCalendarIconClick },
            React.createElement(Icon, { color: "INK_600", size: "MEDIUM" }))));
};
export default React.memo(React.forwardRef((props, ref) => (React.createElement(DateInput, Object.assign({ inputRef: ref || React.createRef() }, props)))));
//# sourceMappingURL=DateInput.js.map